/**
 * ManoCV (My CV) Component
 * 
 * CV and cover letter management page for employees.
 * Allows users to view, create, edit, delete, and set primary CVs and cover letters.
 * 
 * @route /mano-cv
 * @access Employees only
 * 
 * Features:
 * - List of user's CVs with preview cards
 * - Primary (Publikuojama) CV selection (checkbox, can be none)
 *   - At most one CV can be primary at a time
 *   - Primary CV is auto-selected for job applications
 * - Cover letter management
 *   - Primary cover letter selection
 *   - Used as default in job applications
 * - CV actions:
 *   - Create new CV
 *   - Edit existing CV
 *   - Delete CV
 *   - Preview CV
 *   - Print CV
 *   - Download CV
 *   - Email CV
 * 
 * Note:
 * - List order is preserved (creation date DESC)
 * - Primary selection doesn't reorder the list
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Settings, User, ArrowRight, Copy, Printer, Search } from 'lucide-react';
import useConfirmDialog from '../hooks/useConfirmDialog';
import SelectAllToggle from '../components/Common/SelectAllToggle';
import { generatePrintableCV } from '../utils/printableCV';
import api from '../services/api';
import './ManoCV.scss';

interface CV {
  id: number;
  title: string;
  is_primary: boolean;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  cv_language?: string;
  status?: 'draft' | 'pending' | 'published' | 'archived';
  boost_expires_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface CoverLetter {
  id: number;
  title: string;
  content: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

const ManoCV: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'cv' | 'cover-letters'>('cv');
  const [cvs, setCvs] = useState<CV[]>([]);
  const [coverLetters, setCoverLetters] = useState<CoverLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);
  const [editingCoverLetter, setEditingCoverLetter] = useState<CoverLetter | null>(null);
  const [coverLetterForm, setCoverLetterForm] = useState({ title: '', content: '', is_primary: false });
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [selectedCVs, setSelectedCVs] = useState<number[]>([]);
  const [selectedLetters, setSelectedLetters] = useState<number[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchCVs();
      fetchCoverLetters();
    }
  }, [isAuthenticated]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-wrapper')) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdownId]);

  const fetchCVs = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('CV API Response:', result);
        // Ensure we always set an array
        if (Array.isArray(result.data)) {
          setCvs(result.data);
        } else if (Array.isArray(result)) {
          setCvs(result);
        } else {
          console.warn('Unexpected CV response format:', result);
          setCvs([]);
        }
      } else {
        console.error('Failed to fetch CVs:', response.status);
        setCvs([]);
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
      setCvs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoverLetters = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cover-letters/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Cover Letters API Response:', result);
        // Ensure we always set an array
        if (Array.isArray(result.data)) {
          setCoverLetters(result.data);
        } else if (Array.isArray(result)) {
          setCoverLetters(result);
        } else {
          console.warn('Unexpected cover letters response format:', result);
          setCoverLetters([]);
        }
      } else {
        console.error('Failed to fetch cover letters:', response.status);
        setCoverLetters([]);
      }
    } catch (error) {
      console.error('Error fetching cover letters:', error);
      setCoverLetters([]);
    }
  };

  const handleDeleteCV = async (cvId: number) => {
    if (!await confirm('Ar tikrai norite ištrinti šį CV?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv/${cvId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCVs();
      } else {
        showToast('Nepavyko ištrinti CV', 'error');
      }
    } catch (error) {
      console.error('Error deleting CV:', error);
      showToast('Įvyko klaida trinant CV', 'error');
    }
  };

  const handleCopyCV = async (id: number) => {
    try {
      await api.post(`/cv/${id}/duplicate`);
      showToast('CV sėkmingai nukopijuotas!', 'success');
      fetchCVs();
    } catch (error) {
      console.error('Error copying CV:', error);
      showToast('Nepavyko nukopijuoti CV', 'error');
    }
    setOpenDropdownId(null);
  };

  const handlePrintCV = async (id: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Fetch the CV data
      const response = await fetch(`${API_URL}/cv/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const cv = result.data;
        
        // Open CV in new window for printing
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(generatePrintableCV(cv));
          printWindow.document.close();
          printWindow.focus();
          const images = Array.from(printWindow.document.images);
          Promise.all(
            images.map((img) =>
              img.complete
                ? Promise.resolve()
                : new Promise<void>((resolve) => {
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                  })
            )
          ).then(() => printWindow.print());
        }
      } else {
        showToast('Nepavyko gauti CV duomenų', 'error');
      }
    } catch (error) {
      console.error('Error printing CV:', error);
      showToast('Įvyko klaida spausdinant CV', 'error');
    }
    setOpenDropdownId(null);
  };

  const toggleDropdown = (id: number) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleCreateCoverLetter = () => {
    setEditingCoverLetter(null);
    setCoverLetterForm({ title: '', content: '', is_primary: false });
    setShowCoverLetterModal(true);
  };

  const handleEditCoverLetter = (coverLetter: CoverLetter) => {
    setEditingCoverLetter(coverLetter);
    setCoverLetterForm({
      title: coverLetter.title,
      content: coverLetter.content,
      is_primary: coverLetter.is_primary
    });
    setShowCoverLetterModal(true);
  };

  const handleSaveCoverLetter = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const url = editingCoverLetter
        ? `${API_URL}/cover-letters/${editingCoverLetter.id}`
        : `${API_URL}/cover-letters`;
      
      const method = editingCoverLetter ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(coverLetterForm)
      });

      if (response.ok) {
        fetchCoverLetters();
        setShowCoverLetterModal(false);
        setCoverLetterForm({ title: '', content: '', is_primary: false });
      } else {
        showToast('Nepavyko išsaugoti motyvacinio laiško', 'error');
      }
    } catch (error) {
      console.error('Error saving cover letter:', error);
      showToast('Įvyko klaida saugant motyvacinio laiško', 'error');
    }
  };

  const handleDeleteCoverLetter = async (id: number) => {
    if (!await confirm('Ar tikrai norite ištrinti šį motyvacinį laišką?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cover-letters/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCoverLetters();
      } else {
        showToast('Nepavyko ištrinti motyvacinio laiško', 'error');
      }
    } catch (error) {
      console.error('Error deleting cover letter:', error);
      showToast('Įvyko klaida trinant motyvacinį laišką', 'error');
    }
  };

  const handleSetPrimaryCV = async (cv: CV) => {
    const nextPrimary = !cv.is_primary;
    try {
      await api.patch(`/cv/${cv.id}/set-primary`, { is_primary: nextPrimary });
      showToast(nextPrimary ? 'CV publikuojamas' : 'CV nebepublikuojamas', 'success');
      fetchCVs();
    } catch (error) {
      console.error('Error setting primary CV:', error);
      showToast(nextPrimary ? 'Nepavyko publikuoti CV' : 'Nepavyko nuimti publikavimo', 'error');
    }
  };

  const handleSetPrimaryCoverLetter = async (letterId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cover-letters/${letterId}/set-primary`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCoverLetters(); // Refresh the list to show updated primary status
      } else {
        showToast('Nepavyko nustatyti kaip pagrindinio laiško', 'error');
      }
    } catch (error) {
      console.error('Error setting primary cover letter:', error);
      showToast('Įvyko klaida nustatant pagrindinį laišką', 'error');
    }
  };

  // Filter functions
  const filterCVsByTitle = (items: CV[]) => {
    if (!searchTitleFilter.trim()) return items;
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return items.filter(cv => cv.title.toLowerCase().includes(filterLower));
  };

  const filterLettersByTitle = (items: CoverLetter[]) => {
    if (!searchTitleFilter.trim()) return items;
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return items.filter(letter => letter.title.toLowerCase().includes(filterLower));
  };

  // CV selection handlers
  const handleToggleSelectAllCVs = () => {
    const filteredCVs = filterCVsByTitle(cvs);
    if (selectedCVs.length === filteredCVs.length && filteredCVs.length > 0) {
      setSelectedCVs([]);
    } else {
      setSelectedCVs(filteredCVs.map(cv => cv.id));
    }
  };

  const handleToggleSelectCV = (id: number) => {
    setSelectedCVs(prev => 
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelectedCVs = async () => {
    if (selectedCVs.length === 0) return;
    if (!await confirm(`Ar tikrai norite ištrinti ${selectedCVs.length} CV?`)) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await Promise.all(
        selectedCVs.map(id =>
          fetch(`${API_URL}/cv/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
      setSelectedCVs([]);
      fetchCVs();
    } catch (error) {
      console.error('Error deleting CVs:', error);
      showToast('Įvyko klaida trinant CV', 'error');
    }
  };

  // Cover letter selection handlers
  const handleToggleSelectAllLetters = () => {
    const filteredLetters = filterLettersByTitle(coverLetters);
    if (selectedLetters.length === filteredLetters.length && filteredLetters.length > 0) {
      setSelectedLetters([]);
    } else {
      setSelectedLetters(filteredLetters.map(l => l.id));
    }
  };

  const handleToggleSelectLetter = (id: number) => {
    setSelectedLetters(prev => 
      prev.includes(id) ? prev.filter(lid => lid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelectedLetters = async () => {
    if (selectedLetters.length === 0) return;
    if (!await confirm(`Ar tikrai norite ištrinti ${selectedLetters.length} motyvacinius laiškus?`)) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await Promise.all(
        selectedLetters.map(id =>
          fetch(`${API_URL}/cover-letters/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
      setSelectedLetters([]);
      fetchCoverLetters();
    } catch (error) {
      console.error('Error deleting cover letters:', error);
      showToast('Įvyko klaida trinant motyvacinius laiškus', 'error');
    }
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="mano-cv-page">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  if (!isAuthenticated || (user?.user_type !== 'employee' && user?.user_type !== 'job_seeker')) {
    return <Navigate to="/" replace />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const filteredCVs = filterCVsByTitle(cvs);
  const filteredLetters = filterLettersByTitle(coverLetters);

  return (
    <div className="mano-cv-page">
      {ConfirmDialogNode}
      <div className="container">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <span onClick={() => navigate('/')}>Pradžia</span>
          <span className="separator">&gt;</span>
          <span className="current">Mano CV</span>
        </div>

        {/* Tabs row */}
        <div className="tabs-row">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'cv' ? 'active' : ''}`}
              onClick={() => { setActiveTab('cv'); setSearchTitleFilter(''); }}
            >
              Mano CV
              {cvs.length > 0 && <span className="count">{cvs.length}</span>}
            </button>
            <button
              className={`tab ${activeTab === 'cover-letters' ? 'active' : ''}`}
              onClick={() => { setActiveTab('cover-letters'); setSearchTitleFilter(''); }}
            >
              Motyvaciniai laiškai
              {coverLetters.length > 0 && <span className="count">{coverLetters.length}</span>}
            </button>
          </div>
          
          <div className="filters-toolbar">
            <SelectAllToggle
              selectedCount={activeTab === 'cv' ? selectedCVs.length : selectedLetters.length}
              totalCount={activeTab === 'cv' ? filteredCVs.length : filteredLetters.length}
              onToggle={activeTab === 'cv' ? handleToggleSelectAllCVs : handleToggleSelectAllLetters}
            />
            <button 
              className="btn-delete"
              onClick={activeTab === 'cv' ? handleDeleteSelectedCVs : handleDeleteSelectedLetters}
              disabled={activeTab === 'cv' ? selectedCVs.length === 0 : selectedLetters.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder={activeTab === 'cv' ? 'CV pavadinimas' : 'Laiško pavadinimas'}
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <Search size={16} className="search-icon" />
            </div>
            <button 
              className="btn-new-search" 
              onClick={activeTab === 'cv' ? () => navigate('/cv/create') : handleCreateCoverLetter}
            >
              <Plus size={16} />
              <span>{activeTab === 'cv' ? 'Naujas CV' : 'Naujas laiškas'}</span>
            </button>
          </div>
        </div>

        {activeTab === 'cv' && (
          <div className="cv-section">

            {loading ? (
              <div className="loading">Kraunama...</div>
            ) : filteredCVs.length === 0 ? (
              <div className="empty-state">
                <p>{searchTitleFilter ? 'CV pagal šį filtrą nerasta' : 'Dar neturite sukurtų CV'}</p>
                {!searchTitleFilter && (
                  <button className="btn-primary" onClick={() => navigate('/cv/create')}>
                    Sukurti pirmą CV
                  </button>
                )}
              </div>
            ) : (
              <div className="cv-list">
                {filteredCVs.map((cv) => (
                  <div key={cv.id} className={`cv-card ${selectedCVs.includes(cv.id) ? 'selected' : ''}`}>
                    <div className="cv-card-header">
                      <div className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={selectedCVs.includes(cv.id)}
                          onChange={() => handleToggleSelectCV(cv.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="cv-status">
                        <input
                          type="checkbox"
                          checked={cv.is_primary}
                          onChange={() => handleSetPrimaryCV(cv)}
                          title={cv.is_primary ? 'Nebepublikuoti CV' : 'Publikuoti CV'}
                        />
                        <User size={16} />
                        <span className="status-label">
                          {cv.is_primary ? 'Publikuojama' : 'Publikuoti'}
                        </span>
                      </div>
                      <div className="cv-header-actions">
                        <div className="dropdown-wrapper">
                          <button
                            className="btn-icon-small"
                            onClick={() => toggleDropdown(cv.id)}
                            title="Nustatymai"
                          >
                            <Settings size={18} />
                          </button>
                          {openDropdownId === cv.id && (
                            <div className="dropdown-menu">
                              <button onClick={() => { navigate(`/cv/edit/${cv.id}`); setOpenDropdownId(null); }}>
                                <Edit size={16} />
                                Redaguoti
                              </button>
                              <button onClick={() => handleCopyCV(cv.id)}>
                                <Copy size={16} />
                                Kopijuoti
                              </button>
                              <button onClick={() => handlePrintCV(cv.id)}>
                                <Printer size={16} />
                                Spausdinti
                              </button>
                            </div>
                          )}
                        </div>
                        <button
                          className="btn-icon-small"
                          onClick={() => handleDeleteCV(cv.id)}
                          title="Ištrinti"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="cv-card-body">
                      <h3 className="cv-title">{cv.title}</h3>
                      <p className="cv-date">Atnaujinta: {formatDate(cv.updated_at)}</p>
                      {cv.status === 'pending' && (
                        <span className="cv-pending-badge">Laukia patvirtinimo</span>
                      )}
                    </div>

                    <div className="cv-card-posting">
                      <div className="posting-info">
                        {cv.boost_expires_at && new Date(cv.boost_expires_at) > new Date() ? (
                          <>
                            <span className="posting-label">CV iškeltas iki:</span>
                            <span className="posting-date">{formatDate(cv.boost_expires_at)}</span>
                          </>
                        ) : (
                          <span className="posting-label">CV {cv.is_primary ? 'neiškeltas' : 'neiškeltas'}</span>
                        )}
                      </div>
                      <button 
                        className={`btn-extend${!cv.is_primary ? ' disabled' : ''}`}
                        onClick={() => navigate('/employee/services')}
                        disabled={!cv.is_primary}
                        title={!cv.is_primary ? 'Publikuokite CV, kad galėtumėte užsakyti iškėlimą' : ''}
                      >
                        {cv.boost_expires_at && new Date(cv.boost_expires_at) > new Date() 
                          ? 'PRATĘSTI CV IŠKĖLIMĄ' 
                          : 'UŽSAKYTI CV IŠKĖLIMĄ'}
                      </button>
                    </div>

                    <div className="cv-card-languages">
                      <span className="languages-label">Aktyvuotos kalbos:</span>
                      <div className="language-badges">
                        <span className={`language-badge ${!cv.cv_language || cv.cv_language === 'Lietuvių' ? 'active' : ''}`}>LT</span>
                        <span className={`language-badge ${cv.cv_language === 'English' ? 'active' : ''}`}>EN</span>
                      </div>
                    </div>

                    <button className="cv-card-preview" onClick={() => navigate(`/cv/edit/${cv.id}`)}>
                      PERŽIŪRĖTI
                      <ArrowRight size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'cover-letters' && (
          <div className="cover-letters-section">
            {filteredLetters.length === 0 ? (
              <div className="empty-state">
                <p>{searchTitleFilter ? 'Laiškų pagal šį filtrą nerasta' : 'Dar neturite sukurtų motyvacinių laiškų'}</p>
                {!searchTitleFilter && (
                  <button className="btn-primary" onClick={handleCreateCoverLetter}>
                    Sukurti pirmą laišką
                  </button>
                )}
              </div>
            ) : (
              <div className="cover-letter-list">
                {filteredLetters.map((letter) => (
                  <div key={letter.id} className={`cover-letter-card ${selectedLetters.includes(letter.id) ? 'selected' : ''}`}>
                    <div className="letter-card-header">
                      <div className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={selectedLetters.includes(letter.id)}
                          onChange={() => handleToggleSelectLetter(letter.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="letter-status">
                        <input 
                          type="radio" 
                          name="primary-letter" 
                          checked={letter.is_primary}
                          onChange={() => handleSetPrimaryCoverLetter(letter.id)}
                        />
                        <span className="status-label">
                          {letter.is_primary ? 'Pagrindinis' : 'Papildomas'}
                        </span>
                      </div>
                      <div className="letter-header-actions">
                        <button
                          className="btn-icon-small"
                          onClick={() => handleEditCoverLetter(letter)}
                          title="Redaguoti"
                        >
                          <Settings size={18} />
                        </button>
                        <button
                          className="btn-icon-small"
                          onClick={() => handleDeleteCoverLetter(letter.id)}
                          title="Ištrinti"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="letter-card-body">
                      <h3 className="letter-title">{letter.title}</h3>
                      <p className="letter-preview">
                        {letter.content.substring(0, 200)}
                        {letter.content.length > 200 ? '...' : ''}
                      </p>
                      <p className="letter-date">Atnaujinta: {formatDate(letter.updated_at)}</p>
                    </div>

                    <button className="letter-card-preview" onClick={() => handleEditCoverLetter(letter)}>
                      PERŽIŪRĖTI / REDAGUOTI
                      <ArrowRight size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cover Letter Modal */}
        {showCoverLetterModal && (
          <div className="modal-overlay" onClick={() => setShowCoverLetterModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingCoverLetter ? 'Redaguoti motyvacinį laišką' : 'Naujas motyvacinis laiškas'}</h2>
                <button className="btn-close" onClick={() => setShowCoverLetterModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Pavadinimas *</label>
                  <input
                    type="text"
                    value={coverLetterForm.title}
                    onChange={(e) => setCoverLetterForm({ ...coverLetterForm, title: e.target.value })}
                    placeholder="Pvz., Motyvacinis laiškas IT pozicijai"
                  />
                </div>
                <div className="form-group">
                  <label>Tekstas *</label>
                  <textarea
                    value={coverLetterForm.content}
                    onChange={(e) => setCoverLetterForm({ ...coverLetterForm, content: e.target.value })}
                    placeholder="Įveskite motyvacinio laiško tekstą..."
                    rows={12}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={coverLetterForm.is_primary}
                      onChange={(e) => setCoverLetterForm({ ...coverLetterForm, is_primary: e.target.checked })}
                    />
                    Nustatyti kaip pagrindinį
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowCoverLetterModal(false)}>
                  Atšaukti
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSaveCoverLetter}
                  disabled={!coverLetterForm.title || !coverLetterForm.content}
                >
                  {editingCoverLetter ? 'Išsaugoti' : 'Sukurti'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManoCV;

